/**
 * Fact Find section order — global default + per-marketing-firm overrides.
 *
 * The global order lives where it always has: `system_config/required_fields`
 * → `sectionOrder`. Firm overrides live in Firestore
 * `app_settings/fact_find_section_order` as
 * `{ teams: { [marketingTeamId]: string[] }, updatedAt, updatedBy }`.
 *
 * A firm override only applies to MARKETING users viewing a client assigned
 * to that firm (owner decision, Sept 2026). Advisory, paraplanner and
 * super-admin users always see the global order.
 *
 * Plain CommonJS (no requires) so both the CRA bundle and the Node server can
 * load it, like chartStyleDefaults.js.
 */

const FACT_FIND_SECTION_ORDER_DOC = 'app_settings/fact_find_section_order';

/** Canonical section ids in the built-in default order. */
const FACT_FIND_SECTION_IDS = [
  'personal',
  'employment',
  'financial',
  'assets',
  'superannuation',
  'contributions',
  'insurance',
  'beneficiaries',
  'goals',
  'riskProfile',
];

/** Deprecated ids that may still appear in stored orders. */
const DEPRECATED_SECTION_IDS = ['assignment'];

/**
 * Coerce any stored/submitted order into a full permutation of
 * FACT_FIND_SECTION_IDS: unknown ids dropped, duplicates removed, missing
 * sections appended in default order. Returns null when input is not an array.
 */
function normalizeFactFindSectionOrder(order) {
  if (!Array.isArray(order)) return null;
  const seen = new Set();
  const result = [];
  order.forEach((id) => {
    if (typeof id !== 'string') return;
    if (DEPRECATED_SECTION_IDS.includes(id)) return;
    if (!FACT_FIND_SECTION_IDS.includes(id) || seen.has(id)) return;
    seen.add(id);
    result.push(id);
  });
  FACT_FIND_SECTION_IDS.forEach((id) => {
    if (!seen.has(id)) result.push(id);
  });
  return result;
}

/** `true` when a submitted order is exactly a permutation of the known ids. */
function isCompleteFactFindSectionOrder(order) {
  if (!Array.isArray(order)) return false;
  if (order.length !== FACT_FIND_SECTION_IDS.length) return false;
  const normalized = normalizeFactFindSectionOrder(order);
  return normalized.every((id, index) => id === order[index]);
}

/** Per-firm override for a marketing team, or null when the firm uses global. */
function getFirmSectionOrder(settingsDoc, marketingTeamId) {
  if (!marketingTeamId) return null;
  const teams = settingsDoc && typeof settingsDoc === 'object' ? settingsDoc.teams : null;
  if (!teams || typeof teams !== 'object') return null;
  const stored = teams[marketingTeamId];
  return Array.isArray(stored) && stored.length ? normalizeFactFindSectionOrder(stored) : null;
}

/**
 * Effective order for a viewer looking at a client.
 * @param {object} args
 * @param {string[]|null} args.globalOrder — `system_config/required_fields.sectionOrder`
 * @param {object|null} args.settingsDoc — raw `app_settings/fact_find_section_order` doc
 * @param {string|null} args.viewerRole — 'marketing' | 'advisory' | 'paraplanner' | 'super_admin' | null
 * @param {string|null} args.marketingTeamId — the client's `assignment.assignedMarketingTeam`
 */
function resolveFactFindSectionOrder({ globalOrder, settingsDoc, viewerRole, marketingTeamId } = {}) {
  const base = normalizeFactFindSectionOrder(globalOrder) || FACT_FIND_SECTION_IDS.slice();
  if (viewerRole !== 'marketing') return base;
  return getFirmSectionOrder(settingsDoc, marketingTeamId) || base;
}

module.exports = {
  FACT_FIND_SECTION_ORDER_DOC,
  FACT_FIND_SECTION_IDS,
  normalizeFactFindSectionOrder,
  isCompleteFactFindSectionOrder,
  getFirmSectionOrder,
  resolveFactFindSectionOrder,
};
